import { SocialPublishAtField } from '@app/modules/social-export/components/inputs/SocialPublishAtField';
import { TextFieldElementWithEmoji } from '@app/modules/social-export/components/inputs/TextFieldElementWithEmoji';
import { PublishFormUnsavedChangesWarning } from '@app/modules/social-export/components/PublishFormUnsavedChangesWarning';
import { BaseExportFormProps } from '@app/modules/social-export/hooks/social-export-form-props';
import { FormSectionV2 } from '@app/core/components/FormSectionV2';
import { zodResolver } from '@hookform/resolvers/zod';
import { socialExport } from '@mi-front/api-clients';
import { SocialExportTikTokCreatorInfoDto } from '@mi-front/api-v2';
import { Grid, Stack } from '@mui/material';
import { FormContainer } from 'react-hook-form-mui';
import { FormattedMessage } from 'react-intl';
import { TikTokPublishValues, useTiktokPublishSchema } from '../useTiktokPublishSchema';
import { TiktokDisclosureSettings } from './disclosure/TiktokDisclosureSettings';
import { TikTokPublishFormActions } from './TikTokPublishFormActions';
import { TikTokSettingsFields } from './TiktokSettingsFields';
import { TikTokThumbFields } from './TikTokThumbFields';

export type TikTokPublishFormContentProps = BaseExportFormProps<
  socialExport.TiktokPublishDto,
  socialExport.TiktokSettingsDto
> & {
  creatorInfo: SocialExportTikTokCreatorInfoDto;
};

/**
 * Inner TikTok publish form rendered after creator info has been loaded.
 * Receives creatorInfo from the parent so privacy and interaction options follow TikTok guidelines.
 */
export function TikTokPublishFormContent({
  source,
  onUpload,
  defaultSettings,
  videoUrl,
  creatorInfo,
}: TikTokPublishFormContentProps) {
  const schema = useTiktokPublishSchema();
  const initialValues: TikTokPublishValues = {
    ...defaultSettings,
    title: source.title ?? '',
    thumbType: 'DEFAULT',
    coverOffsetMs: null,
    publishAt: null,
    privacyLevel: undefined!,
    allowComment: false,
    allowDuet: false,
    allowStitch: false,
    discloseVideoContent: false,
    brandContent: false,
    brandOrganic: false,
  } as TikTokPublishValues;

  const beforeSubmit = (data: TikTokPublishValues): socialExport.TiktokPublishDto => {
    const hasDisclosure = data.discloseVideoContent && (data.brandContent || data.brandOrganic);
    return {
      title: data.title,
      privacyLevel: data.privacyLevel as socialExport.TiktokPublishDtoPrivacyLevelEnumKey,
      allowComment: !data.allowComment,
      allowDuet: !data.allowDuet,
      allowStitch: !data.allowStitch,
      publishAt: data.publishAt || undefined,
      coverOffsetMs:
        data.thumbType === 'VIDEO_FRAME' && data.coverOffsetMs ? data.coverOffsetMs : undefined,
      ...(hasDisclosure && {
        brandContent: data.brandContent ?? false,
        brandOrganic: data.brandOrganic ?? false,
      }),
    } as socialExport.TiktokPublishDto;
  };

  return (
    <FormContainer
      values={initialValues}
      resolver={zodResolver(schema)}
      mode="onBlur"
      onSuccess={async (formData) => {
        await onUpload(beforeSubmit(formData));
      }}
    >
      <PublishFormUnsavedChangesWarning />
      <FormSectionV2
        title={<FormattedMessage id="socialExport.form.content" defaultMessage="Contenido" />}
      >
        <Stack spacing={3}>
          <TextFieldElementWithEmoji
            name="title"
            label={<FormattedMessage id="socialExport.form.title" defaultMessage="Título" />}
            required
            fullWidth
            multiline
            rows={3}
          />
        </Stack>
      </FormSectionV2>
      <FormSectionV2
        title={<FormattedMessage id="socialExport.form.thumbnail" defaultMessage="Thumbnail" />}
      >
        <Grid container spacing={4}>
          <Grid item md={6}>
            <TikTokThumbFields videoUrl={videoUrl} />
          </Grid>
        </Grid>
      </FormSectionV2>
      <FormSectionV2
        title={
          <FormattedMessage
            id="socialExport.form.settings"
            defaultMessage="Parámetros de publicación"
          />
        }
      >
        <Stack spacing={2}>
          <TikTokSettingsFields creatorInfo={creatorInfo} />
          <TiktokDisclosureSettings />
        </Stack>
      </FormSectionV2>
      <FormSectionV2
        title={<FormattedMessage id="socialExport.form.schedule" defaultMessage="Programación" />}
      >
        <Grid container>
          <Grid item md={12}>
            <SocialPublishAtField />
          </Grid>
        </Grid>
      </FormSectionV2>
      <TikTokPublishFormActions />
    </FormContainer>
  );
}
